import { Component, Inject, OnInit, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Subscription } from 'rxjs';
import { UsersService, AuthService, SnackbarService } from '@/shared/services';
import {
  FrontEndToolPermission,
  FrontEndToolPermissionGroup,
  UserUpdatePayload,
  UserReturn,
  RoleAttributes,
} from '../../../../server/types';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.css'],
  standalone: false,
})
export class UserDialogComponent implements OnInit {
  public oldPassword: string = '';
  public newPassword: string = '';
  public newPasswordConfirm: string = '';
  public userUpdateForm!: FormGroup;
  public currentTab: string = 'Update Info';
  public passwordErrorMsg: string = '';
  public showPasswordError: boolean = false;
  public userPermissionsListGroups: FrontEndToolPermissionGroup[] = [];
  public userPermissionList: FrontEndToolPermission[] = [];
  public userRoles!: WritableSignal<RoleAttributes[]>;
  public adminRoleSelected = false;

  constructor(
    private usersService: UsersService,
    public authService: AuthService,
    public dialogRef: MatDialogRef<UserDialogComponent>,
    private snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public userData: UserReturn,
  ) {}

  ngOnInit(): void {
    this.userUpdateForm = new FormGroup({
      username: new FormControl(this.userData.username),
      first_name: new FormControl(this.userData.first_name),
      password: new FormControl('', [Validators.required]),
      new_password: new FormControl('', [Validators.required]),
      password_confirm: new FormControl('', [Validators.required]),
      last_name: new FormControl(this.userData.last_name),
      role_id: new FormControl(this.userData.role.id),
      permissions: new FormControl<string[]>([]),
    });

    // getting all permission options
    this.usersService
      .getUserGroupedPermissionsList()
      .subscribe((permissionListGroup) => {
        this.userPermissionsListGroups = permissionListGroup;

        this.userPermissionsListGroups.forEach((group) => {
          this.userPermissionList.push(...group.tools);
        });

        this.userPermissionList.forEach((rec) => {
          // check if user is an admin or pamlab
          // set all permission to active
          if (
            this.userData.role.name === 'admin' ||
            this.userData.role.name === 'pamlab'
          ) {
            rec.active = true;
            return;
          }

          const userPermission = this.userData.permissions.find(
            (x) => x.name === rec.name,
          );

          // if userPermission is found set permission to userPermission active status
          if (userPermission) {
            rec.active = userPermission.active;
          }
        });

        this.userUpdateForm.patchValue({
          permissions: this.userPermissionList
            .filter((rec) => {
              if (
                this.userData.role.name === 'admin' ||
                this.userData.role.name === 'pamlab'
              ) {
                return true;
              } else {
                return (
                  this.userData.permissions.find((x) => rec.name === x.name) !==
                  undefined
                );
              }
            })
            .map((rec) => rec.name),
        });
      });

    this.userRoles = this.authService.getRoles();
    const role = this.userRoles().find((r) => r.id === this.userData.role.id);
    this.adminRoleSelected = role?.name === 'pamlab' || role?.name === 'admin';
  }

  hasPermission(permissionName: string) {
    if (
      this.userData.role.name === 'admin' ||
      this.userData.role.name === 'pamlab'
    ) {
      return true;
    }

    return this.userUpdateForm
      .get('permissions')
      ?.value.includes(permissionName);
  }

  submit() {
    const payload: UserUpdatePayload = {};
    const selectedPermissions: FrontEndToolPermission[] =
      this.userPermissionList.filter((x) =>
        this.userUpdateForm.get('permissions')?.value.includes(x.name),
      );

    if (this.currentTab === 'Update Info') {
      payload.first_name = this.userUpdateForm.controls['first_name'].value;
      payload.last_name = this.userUpdateForm.controls['last_name'].value;
      payload.role_id = this.userUpdateForm.controls['role_id'].value;
      payload.username = this.userUpdateForm.controls['username'].value;
      payload.permissions = selectedPermissions;

      this.usersService.updateUser(this.userData.id, payload).subscribe({
        next: (userInfo) => {
          this.usersService.setUser(userInfo);
          this.dialogRef.close();
        },
        error: (error: HttpErrorResponse) => {
          this.showPasswordError = true;
          this.passwordErrorMsg = error.message;
        },
      });
    } else {
      if (!this.userUpdateForm.controls['new_password'].value.length) {
        this.showPasswordError = true;
        this.passwordErrorMsg = 'Please enter a password';
        return;
      }
      if (
        this.userUpdateForm.controls['new_password'].value !==
        this.userUpdateForm.controls['password_confirm'].value
      ) {
        this.showPasswordError = true;
        this.passwordErrorMsg = "Passwords don't match";
        return;
      }
      this.authService
        .resetPassword({
          password: this.userUpdateForm.controls['new_password'].value,
          user_id: this.userData.id,
        })
        .subscribe({
          next: () => {
            this.snackbarService.open('Password reset successfully');
            this.dialogRef.close();
          },
        });
    }
  }

  handleTabChange(event: MatTabChangeEvent): void {
    this.currentTab = event.tab.textLabel;
    if (this.currentTab === 'Update Info') {
      this.userUpdateForm.controls['password'].reset();
      this.userUpdateForm.controls['password_confirm'].reset();
      this.userUpdateForm.controls['new_password'].reset();
      this.showPasswordError = false;
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onToggleChange(permission: FrontEndToolPermission) {
    permission.active = !permission.active;
  }

  // Check if all options are selected
  isAllSelected() {
    const selectedValues = this.userUpdateForm.get('permissions')?.value || [];
    return selectedValues.length === this.userPermissionList.length;
  }

  onCheckboxChange(event: MatCheckboxChange) {
    const isChecked = event.checked;

    if (isChecked) {
      if (this.userPermissionList.length) {
        this.userPermissionList = [];
      }

      this.userPermissionsListGroups.forEach((group) => {
        this.userPermissionList.push(...group.tools);
      });

      this.userPermissionList.forEach((permission) => {
        // check if user is an admin or pamlab
        // set all permission to active
        if (
          this.userData.role.name === 'admin' ||
          this.userData.role.name === 'pamlab'
        ) {
          permission.active = true;
        }

        if (permission) {
          permission.active = true;
        }
      });

      this.userUpdateForm.patchValue({
        permissions: this.userPermissionList.map(
          (permission) => permission.name,
        ),
      });
    } else {
      this.userUpdateForm.patchValue({ permissions: [] });
    }
  }

  shouldShowToggle(permissionValue: string) {
    return (this.userUpdateForm.get('permissions')!.value as string[]).includes(
      permissionValue,
    );
  }

  handleRoleChange(roleId: number) {
    this.adminRoleSelected = false;
    const role = this.userRoles().find((r) => r.id === roleId);

    if (role?.name === 'pamlab' || role?.name === 'admin') {
      // Handle admin case here
      this.adminRoleSelected = true;
      this.userUpdateForm.patchValue({
        permissions: [],
      });
      return;
    }

    this.authService.getDefaultRolePermissionsByRoleId(roleId).subscribe({
      next: (permissions) => {
        this.userUpdateForm.patchValue({
          permissions: permissions.map((p) => p.name),
        });
      },
    });
  }
}
